import { useCallback, useEffect, useRef, useState } from 'react'
import { SectionCompletionState } from '@assessments/shared'
import { isSessionExpired } from '../../services/apiError.js'
import { CompletionValidationError, fetchSectionCompletion, setSectionCompletion } from './api.js'

export interface UseSectionCompletion {
  /** Current completion state; `not_started` until loaded / when unrecorded. */
  state: SectionCompletionState
  loading: boolean
  /** A transition (mark complete / re-open) is in flight. */
  pending: boolean
  /** Transport/persistence error from the last transition; null when clear. */
  error: string | null
  /** Outstanding required question ids from a rejected mark-complete (FR-011). */
  unansweredRequired: string[]
  /** Mark complete; resolves true on success (caller navigates to the overview). */
  markComplete: () => Promise<boolean>
  /** Re-open for editing; resolves true on success. */
  reopen: () => Promise<boolean>
}

/**
 * Owns a section's completion state and the two transitions (EA-62). Loads the
 * current state on mount, then updates optimistically from each transition's result.
 * Separates the required-answered gate (422 → `unansweredRequired`) from a
 * transport/persistence failure (`error`) so the section view can message each
 * correctly (FR-010/FR-011), and a dead session (401) from both — a transition that
 * failed because the user is no longer signed in is not a persistence problem and
 * must not be reported as one.
 */
export function useSectionCompletion(
  organizationSurveyId: string,
  sectionId: string,
  onSessionExpired?: () => void,
): UseSectionCompletion {
  const [state, setState] = useState<SectionCompletionState>(SectionCompletionState.NotStarted)
  const [loading, setLoading] = useState(true)
  const [pending, setPending] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [unansweredRequired, setUnansweredRequired] = useState<string[]>([])

  const requestId = useRef(0)
  // Held in a ref so a caller passing an inline callback doesn't re-create `transition`.
  const onSessionExpiredRef = useRef(onSessionExpired)
  onSessionExpiredRef.current = onSessionExpired

  useEffect(() => {
    const id = ++requestId.current
    setLoading(true)
    void (async () => {
      try {
        const record = await fetchSectionCompletion(organizationSurveyId, sectionId)
        if (id === requestId.current) setState(record.state)
      } catch {
        // Leave the default state; the transition attempt will surface any real error.
      } finally {
        if (id === requestId.current) setLoading(false)
      }
    })()
  }, [organizationSurveyId, sectionId])

  const transition = useCallback(
    async (
      target: SectionCompletionState.Complete | SectionCompletionState.InProgress,
    ): Promise<boolean> => {
      setPending(true)
      setError(null)
      setUnansweredRequired([])
      try {
        const record = await setSectionCompletion(organizationSurveyId, sectionId, target)
        setState(record.state)
        return true
      } catch (err: unknown) {
        if (err instanceof CompletionValidationError) {
          setUnansweredRequired(err.unansweredRequiredQuestionIds)
        } else if (isSessionExpired(err)) {
          onSessionExpiredRef.current?.()
        } else {
          setError(err instanceof Error ? err.message : String(err))
        }
        return false
      } finally {
        setPending(false)
      }
    },
    [organizationSurveyId, sectionId],
  )

  const markComplete = useCallback(() => transition(SectionCompletionState.Complete), [transition])
  const reopen = useCallback(() => transition(SectionCompletionState.InProgress), [transition])

  return { state, loading, pending, error, unansweredRequired, markComplete, reopen }
}
